import { useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { fetchSlotHold } from '@/features/campaigns/api/campaignApi';

const RESYNC_MS = 30_000;

type HoldState = {
  active: boolean;
  secondsLeft: number;
  /** A hold that was running has ended on the server (expired or released). */
  lost: boolean;
};

/**
 * The server's hold on a campaign's slots. `secondsLeft` comes from the server clock, so the timer
 * shows real time and survives a page reload. It is null until the server has reported a hold, in
 * which case the caller falls back to a local countdown. `lost` turns true once a hold that was
 * running has ended on the server.
 *
 * `refreshKey` changes whenever the plan changes, because saving a plan renews the hold.
 * `expireIfMissing` treats "no hold" as an ended session even on the first look (an AI plan always
 * has a hold); without it, only a hold seen running and then gone counts as ended.
 */
export function useSlotHold(campaignId: string | undefined, refreshKey: unknown, expireIfMissing = false) {
  const queryClient = useQueryClient();
  const queryKey = ['slot-hold', campaignId];

  const query = useQuery({
    queryKey,
    enabled: Boolean(campaignId),
    refetchInterval: RESYNC_MS,
    refetchOnWindowFocus: true,
    staleTime: 0,
    queryFn: async (): Promise<HoldState> => {
      const previous = queryClient.getQueryData<HoldState>(queryKey);
      const data = (await fetchSlotHold(String(campaignId))).data;
      const active = Boolean(data?.active);
      const known = data?.available !== false;
      return {
        active,
        secondsLeft: active ? Number(data?.secondsLeft ?? 0) : 0,
        lost: !active && known && (expireIfMissing || Boolean(previous?.active || previous?.lost)),
      };
    },
  });

  const { refetch } = query;
  useEffect(() => {
    if (campaignId) void refetch();
  }, [campaignId, refreshKey, refetch]);

  return {
    secondsLeft: query.data?.active ? query.data.secondsLeft : null,
    lost: Boolean(query.data?.lost),
  };
}
